import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, Sparkles, Clock, AlertCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';

import subscriptionVerificationService from '../../services/subscriptionVerificationService';
import Button from '../../components/ui/Button';
import CelebrationHeader from './components/CelebrationHeader';
import DetailedReceipt from './components/DetailedReceipt';
import FeatureActivationCards from './components/FeatureActivationCards';
import GuidedOnboarding from './components/GuidedOnboarding';
import SupportContact from './components/SupportContact';
import { trackPremiumUpgrade } from '../../hooks/useGoogleAnalytics';

const PremiumUpgradeSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState('Verifying your subscription...');

  // Get payment details from navigation state
  const paymentDetails = location?.state?.paymentDetails;
  const expectedPlanType = location?.state?.planType;

  useEffect(() => {
    verifyAndLoadSubscription();
  }, []);

  const verifyAndLoadSubscription = async () => {
    setLoading(true);
    setVerifying(true);
    setError(null);

    try {
      // Step 1: Poll for subscription activation
      setVerificationStatus('Confirming payment with Stripe...');
      
      const { verified, subscription: verifiedSub, error: verifyError } = 
        await subscriptionVerificationService?.pollSubscriptionStatus(expectedPlanType, {
          maxAttempts: 20,
          intervalMs: 2000,
          timeoutMs: 40000,
          onAttempt: (attempt) => {
            setVerificationAttempts(attempt);
            if (attempt > 5) {
              setVerificationStatus('Syncing subscription data...');
            }
            if (attempt > 10) {
              setVerificationStatus('Almost there, finalizing activation...');
            }
          }
        });

      if (verifyError) {
        setError(verifyError);
        setVerifying(false);
        setLoading(false);
        return;
      }

      if (!verified) {
        setError('Subscription verification incomplete. Please refresh the page or check your subscription management.');
        setVerifying(false);
        setLoading(false);
        return;
      }

      // Step 2: Subscription verified successfully
      setVerificationStatus('Subscription activated!');
      setSubscription(verifiedSub);
      setVerifying(false);

      // Track premium upgrade conversion in GA4
      trackPremiumUpgrade(
        expectedPlanType || verifiedSub?.plan_type || 'premium',
        verifiedSub?.billing_interval || 'monthly'
      );

    } catch (err) {
      console.error('Failed to verify subscription:', err);
      setError('Failed to verify subscription. Please check your subscription management.');
      setVerifying(false);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = () => {
    setShowOnboarding(true);
  };

  const handleCompleteOnboarding = () => {
    setShowOnboarding(false);
    navigate('/daily-check-in');
  };

  const handleSkipToDashboard = () => {
    navigate('/daily-check-in');
  };

  const handleRetryVerification = () => {
    verifyAndLoadSubscription();
  };

  // Verification in progress
  if (verifying) {
    return (
      <>
        <Helmet>
          <title>Verifying Subscription - MyBodyToday</title>
        </Helmet>
        
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
              <Clock className="w-10 h-10 text-blue-600 animate-pulse" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Verifying Your Subscription
            </h2>
            
            <p className="text-gray-600 mb-6">
              {verificationStatus}
            </p>

            <div className="mb-6">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>

            {verificationAttempts > 0 && (
              <p className="text-sm text-gray-500">
                Verification attempt {verificationAttempts} of 20
              </p>
            )}

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What's happening?</strong><br />
                We're confirming your payment with Stripe and activating your premium features. This usually takes 5-10 seconds.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your Premium experience...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Verification In Progress - MyBodyToday</title>
        </Helmet>
        
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verification In Progress</h2>
            
            <p className="text-gray-600 mb-6">
              {error}
            </p>

            <div className="space-y-3">
              <Button 
                onClick={handleRetryVerification} 
                variant="default"
                fullWidth
              >
                Retry Verification
              </Button>
              
              <Button 
                onClick={() => navigate('/subscription-management')} 
                variant="outline"
                fullWidth
              >
                Go to Subscription Management
              </Button>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg text-left">
              <p className="text-sm text-gray-700">
                <strong>Don't worry!</strong> Your payment was successful. The subscription activation may take a few moments to sync. You can:
              </p>
              <ul className="text-sm text-gray-700 mt-2 space-y-1 list-disc list-inside">
                <li>Wait 30 seconds and click "Retry Verification"</li>
                <li>Check your subscription status in Subscription Management</li>
                <li>Contact support if the issue persists</li>
              </ul>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <CheckCircle2 size={48} className="mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Details</h2>
          <p className="text-gray-600 mb-4">Subscription not found</p>
          <Button onClick={() => navigate('/subscription-management')} variant="default">
            Go to Subscription Management
          </Button>
        </div>
      </div>
    );
  }

  const planAmount = subscription?.billingInterval === 'yearly' 
    ? subscription?.plan?.priceYearly 
    : subscription?.plan?.priceMonthly;

  return (
    <>
      <Helmet>
        <title>Welcome to Premium - MyBodyToday</title>
        <meta name="description" content="Your Premium subscription is now active. Explore your new features and benefits." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
        {/* Celebration Header */}
        <CelebrationHeader planName={subscription?.plan?.name} />

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          {/* Detailed Receipt */}
          <DetailedReceipt 
            subscription={subscription}
            planAmount={planAmount}
            paymentMethod={paymentDetails?.paymentMethod}
          />

          {/* Feature Activation Cards */}
          <FeatureActivationCards 
            plan={subscription?.plan}
            onExploreFeature={(feature) => navigate(feature?.path)}
          />

          {/* Guided Onboarding Section */}
          {!showOnboarding ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Sparkles className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready to Get Started?</h2>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Take a quick tour of your new Premium features and learn how to make the most of your subscription.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={handleStartOnboarding}
                  variant="default"
                  size="lg"
                  iconName="Play"
                  iconPosition="left"
                >
                  Start Feature Tour
                </Button>
                <Button 
                  onClick={handleSkipToDashboard}
                  variant="outline"
                  size="lg"
                >
                  Skip to Dashboard
                </Button>
              </div>
            </div>
          ) : (
            <GuidedOnboarding 
              plan={subscription?.plan}
              onComplete={handleCompleteOnboarding}
            />
          )}

          {/* Support Contact */}
          <SupportContact />
        </div>
      </div>
    </>
  );
};

export default PremiumUpgradeSuccess;